/**
 * slugify — Convert a mask name to a URL-safe slug.
 *
 * Rules:
 *   - Lowercase
 *   - Spaces, underscores → hyphens
 *   - Remove special characters (keep alphanumerics, hyphens, and digits)
 *   - Collapse consecutive hyphens
 *   - Trim leading/trailing hyphens
 *   - Truncate to SLUG_MAX_LENGTH chars, breaking at word boundary
 *   - For excessively multi-word names (translation-concatenated), keep
 *     only the first few words — the rest are translations of the same term
 *
 * Collision detection:
 *   Pass a `seen` Set to detect duplicates. When a collision is found,
 *   appends `-{mask.id}` to guarantee uniqueness.
 *
 * Usage:
 *   const slug = slugify('Star Outline')  // → 'star-outline'
 *   const slug = slugify('Heart!!!', seen, 42)  // → 'heart' (or 'heart-42' on collision)
 *
 * @param {string}  name  — The mask name to slugify
 * @param {Set}     seen  — Optional Set of already-used slugs for collision detection
 * @param {number}  id    — Optional mask id, used as suffix when collision detected
 * @returns {string} URL-safe unique slug
 */

const SLUG_MAX_LENGTH = 80
const MAX_WORDS = 6   // beyond this, treat as translation-concatenation and trim

export function slugify(name, seen, id) {
  let raw = String(name || '').trim()

  // Detect translation-concatenation: if the name has many words,
  // keep only the first few (the first language translation).
  const words = raw.split(/[\s]+/)
  if (words.length > MAX_WORDS) {
    raw = words.slice(0, 3).join(' ')
  }

  let slug = raw
    .toLowerCase()
    .replace(/[_\s]+/g, '-')         // spaces and underscores → hyphen
    .replace(/[^\w-]/g, '')           // strip anything not word char or hyphen
    .replace(/-{2,}/g, '-')          // collapse consecutive hyphens
    .replace(/^-+/, '')              // trim leading hyphens
    .replace(/-+$/, '')              // trim trailing hyphens

  // Truncate to max length, breaking at a hyphen boundary
  if (slug.length > SLUG_MAX_LENGTH) {
    const cut = slug.lastIndexOf('-', SLUG_MAX_LENGTH)
    slug = cut > 0 ? slug.slice(0, cut) : slug.slice(0, SLUG_MAX_LENGTH)
  }

  // Fallback for empty / garbage names
  if (!slug) {
    slug = id ? `shape-${id}` : 'shape'
  }

  // Collision detection
  if (seen) {
    if (seen.has(slug) && id != null) {
      slug = `${slug}-${id}`
    }
    seen.add(slug)
  }

  return slug
}

/**
 * Extract a readable display name from a filename-style string.
 * Used as a fallback when mask.name and mask.title are absent.
 *
 *   filenameToDisplay('my-cool-mask') → 'My Cool Mask'
 *
 * @param {string} filename
 * @returns {string}
 */
export function filenameToDisplay(filename) {
  return (filename || '')
    .split('/').pop()                // drop path prefix
    .split('?')[0]                   // drop query string
    .split('.')[0]                   // drop extension
    .replace(/[-_]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim()
}

export default slugify
